// @ts-nocheck
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { formatMoney, formatDate, cn } from "@/lib/utils";
import {
  TrendingUp,
  DollarSign,
  PackageCheck,
  Calculator,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  FileCheck,
  Truck,
  Layers,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { QuickReceiptDialog } from "@/pages/finance/receipts/components/QuickReceiptDialog";
import EntityLink from "@/components/shared/EntityLink";

interface OrderSettlementTabProps {
  orderId: string;
  orderCode?: string;
  customerId?: string;
}

export function OrderSettlementTab({
  orderId,
  orderCode,
  customerId,
}: OrderSettlementTabProps) {
  const queryClient = useQueryClient();
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [closeNotes, setCloseNotes] = useState("");
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["order-settlement", orderId],
    queryFn: async () =>
      trpcClient.orders.getSettlementSummary.query({ id: orderId }),
  });

  const closeOrderMutation = useMutation({
    mutationFn: async () => {
      return await trpcClient.orders.closeOrder.mutate({
        id: orderId,
        notes: closeNotes || undefined,
      });
    },
    onSuccess: (res: any) => {
      toast.success(
        `Đã quyết toán và đóng đơn hàng ${res.orderCode || orderCode} thành công!`
      );
      queryClient.invalidateQueries({ queryKey: ["order", orderId] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["order-settlement", orderId] });
      queryClient.invalidateQueries({ queryKey: ["customer"] });
      queryClient.invalidateQueries({ queryKey: ["ar-reconciliations"] });
      setShowCloseConfirm(false);
    },
    onError: (err: any) => {
      toast.error(err.message || "Lỗi khi quyết toán đóng đơn hàng");
    },
  });

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mb-3" />
        <p className="text-xs text-slate-500 font-medium">
          Đang tính toán quyết toán và phân tích P&L đơn hàng...
        </p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-8 text-center text-rose-500 text-xs">
        Không thể tải dữ liệu quyết toán đơn hàng.
      </div>
    );
  }

  const { order, fulfillment, financials, costAndProfit, gate } = data as any;
  const isSettled = order.status === "COMPLETED";

  return (
    <div className="space-y-4 text-xs">
      {/* ═══ TOP STATUS BANNER ═══ */}
      {isSettled ? (
        <div className="flex items-center justify-between p-3.5 bg-emerald-50/90 border border-emerald-200 rounded-xl">
          <div className="flex items-center gap-3">
            <span className="p-2 bg-emerald-100 text-emerald-700 rounded-lg">
              <CheckCircle2 className="w-5 h-5" />
            </span>
            <div>
              <h4 className="font-bold text-emerald-900 text-sm">
                Đơn Hàng Đã Quyết Toán & Đóng Thành Công (Settled & Closed)
              </h4>
              <p className="text-[11px] text-emerald-700">
                Toàn bộ sản lượng giao nhận, chi phí sản xuất và công nợ đã được chốt sổ .
              </p>
            </div>
          </div>
          <Badge className="bg-emerald-600 text-white border-none font-semibold px-2.5 py-1">
            ĐÃ ĐÓNG
          </Badge>
        </div>
      ) : !gate.canClose ? (
        <div className="flex items-start justify-between p-3.5 bg-amber-50/90 border border-amber-200 rounded-xl">
          <div className="flex items-start gap-3">
            <span className="p-2 bg-amber-100 text-amber-700 rounded-lg mt-0.5">
              <ShieldAlert className="w-5 h-5" />
            </span>
            <div>
              <h4 className="font-bold text-amber-900 text-sm">
                Đơn Hàng Đang Trong Tiến Trình Quyết Toán (Chưa Đủ Điều Kiện Đóng)
              </h4>
              <ul className="list-disc list-inside mt-1 space-y-0.5 text-[11px] text-amber-800">
                {gate.blockingReasons.map((reason: string, idx: number) => (
                  <li key={idx}>{reason}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="flex gap-2">
            {financials.balanceDue > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 font-semibold"
                onClick={() => setShowReceiptDialog(true)}
              >
                <DollarSign className="w-3.5 h-3.5 mr-1" /> Thu Tiền Nhanh
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between p-3.5 bg-indigo-50/90 border border-indigo-200 rounded-xl">
          <div className="flex items-center gap-3">
            <span className="p-2 bg-indigo-100 text-indigo-700 rounded-lg">
              <FileCheck className="w-5 h-5" />
            </span>
            <div>
              <h4 className="font-bold text-indigo-900 text-sm">
                Sẵn Sàng Quyết Toán & Đóng Đơn Hàng (Ready for Closing Gate)
              </h4>
              <p className="text-[11px] text-indigo-700">
                100% Lệnh sản xuất đã hoàn tất, tỷ lệ giao hàng đạt chuẩn dung sai và số liệu đã sẵn sàng để khóa sổ.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {financials.balanceDue > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 font-semibold"
                onClick={() => setShowReceiptDialog(true)}
              >
                <DollarSign className="w-3.5 h-3.5 mr-1" /> Thu Tiền Nhanh
              </Button>
            )}
            <Button
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-xs"
              onClick={() => setShowCloseConfirm(true)}
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Quyết Toán & Đóng Đơn
            </Button>
          </div>
        </div>
      )}

      {/* ═══ 4 KPI CARDS: DOANH THU, CHI PHÍ, P&L, DỰ TOÁN ═══ */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {/* KPI 1: Doanh Thu Thực Giao */}
        <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-xs">
          <div className="flex items-center justify-between text-slate-500 mb-1">
            <span className="text-[11px] font-medium uppercase tracking-wider">
              Doanh Thu Thực Giao
            </span>
            <span className="p-1 rounded-md bg-blue-50 text-blue-600">
              <DollarSign className="w-3.5 h-3.5" />
            </span>
          </div>
          <div className="text-base font-bold text-slate-900 font-mono">
            {formatMoney(financials.realizedRevenue)} đ
          </div>
          <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500">
            <span>Đơn hàng: {formatMoney(financials.orderTotal)} đ</span>
            <span className="font-semibold text-emerald-600">
              {financials.orderTotal > 0
                ? `${Math.round((financials.realizedRevenue / financials.orderTotal) * 100)}%`
                : "100%"}
            </span>
          </div>
        </div>

        {/* KPI 2: Chi Phí Thực Tế */}
        <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-xs">
          <div className="flex items-center justify-between text-slate-500 mb-1">
            <span className="text-[11px] font-medium uppercase tracking-wider">
              Chi Phí Thực Tế
            </span>
            <span className="p-1 rounded-md bg-rose-50 text-rose-600">
              <Calculator className="w-3.5 h-3.5" />
            </span>
          </div>
          <div className="text-base font-bold text-rose-600 font-mono">
            {formatMoney(costAndProfit.actualTotalCost)} đ
          </div>
          <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500">
            <span>Dự toán BOM: {formatMoney(costAndProfit.estimatedTotalCost)} đ</span>
            <span
              className={cn(
                "font-semibold",
                costAndProfit.costVariance > 0
                  ? "text-rose-600"
                  : "text-emerald-600"
              )}
            >
              {costAndProfit.costVariance > 0 ? "+" : ""}
              {formatMoney(costAndProfit.costVariance)} đ
            </span>
          </div>
        </div>

        {/* KPI 3: Lợi Nhuận Gộp (P&L) */}
        <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-xs">
          <div className="flex items-center justify-between text-slate-500 mb-1">
            <span className="text-[11px] font-medium uppercase tracking-wider">
              Lợi Nhuận Gộp (P&L)
            </span>
            <span
              className={cn(
                "p-1 rounded-md",
                costAndProfit.grossProfit >= 0
                  ? "bg-emerald-50 text-emerald-600"
                  : "bg-rose-50 text-rose-600"
              )}
            >
              <TrendingUp className="w-3.5 h-3.5" />
            </span>
          </div>
          <div
            className={cn(
              "text-base font-bold font-mono",
              costAndProfit.grossProfit >= 0
                ? "text-emerald-600"
                : "text-rose-600"
            )}
          >
            {formatMoney(costAndProfit.grossProfit)} đ
          </div>
          <div className="mt-1 flex items-center justify-between text-[11px]">
            <span className="text-slate-500">Tỷ suất LN (Margin):</span>
            <span
              className={cn(
                "font-bold px-1.5 py-0.5 rounded text-[10px]",
                costAndProfit.grossMarginPct >= 20
                  ? "bg-emerald-100 text-emerald-800"
                  : costAndProfit.grossMarginPct >= 10
                    ? "bg-amber-100 text-amber-800"
                    : "bg-rose-100 text-rose-800"
              )}
            >
              {costAndProfit.grossMarginPct}%
            </span>
          </div>
        </div>

        {/* KPI 4: Tiến Độ Thanh Toán */}
        <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-xs">
          <div className="flex items-center justify-between text-slate-500 mb-1">
            <span className="text-[11px] font-medium uppercase tracking-wider">
              Công Nợ Còn Lại
            </span>
            <span
              className={cn(
                "p-1 rounded-md",
                financials.balanceDue === 0
                  ? "bg-emerald-50 text-emerald-600"
                  : "bg-amber-50 text-amber-600"
              )}
            >
              <DollarSign className="w-3.5 h-3.5" />
            </span>
          </div>
          <div
            className={cn(
              "text-base font-bold font-mono",
              financials.balanceDue > 0 ? "text-amber-600" : "text-emerald-600"
            )}
          >
            {formatMoney(financials.balanceDue)} đ
          </div>
          <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500">
            <span>Đã thu: {formatMoney(financials.totalPaid)} đ</span>
            <span className="font-semibold text-slate-700">
              {financials.realizedRevenue > 0
                ? `${Math.round((financials.totalPaid / financials.realizedRevenue) * 100)}%`
                : "100%"}
            </span>
          </div>
        </div>
      </div>

      {/* ═══ BẢNG ĐỐI SOÁT GIAO NHẬN & DUNG SAI SẢN LƯỢNG ═══ */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs">
        <div className="p-3 bg-slate-50/60 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="p-1 rounded bg-indigo-100 text-indigo-700">
              <PackageCheck className="w-4 h-4" />
            </span>
            <h3 className="font-semibold text-slate-800 text-xs">
              Đối Soát Sản Lượng Giao Nhận & Đánh Giá Dung Sai (Fulfillment & Tolerance Audit)
            </h3>
          </div>
          <div className="text-[11px] text-slate-500">
            Dung sai cho phép:{" "}
            <span className="font-bold text-slate-700">
              ±{fulfillment.tolerancePercent}%
            </span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/40 text-[11px]">
                <TableHead className="w-[50px] text-center">STT</TableHead>
                <TableHead className="w-[120px]">Mã SP</TableHead>
                <TableHead className="min-w-[200px]">Tên Sản Phẩm</TableHead>
                <TableHead className="w-[80px] text-center">ĐVT</TableHead>
                <TableHead className="w-[100px] text-right">SL Đặt</TableHead>
                <TableHead className="w-[100px] text-right font-semibold text-slate-800">
                  SL Thực Giao
                </TableHead>
                <TableHead className="w-[100px] text-right">Còn Lại</TableHead>
                <TableHead className="w-[110px] text-right">Thành Tiền</TableHead>
                <TableHead className="w-[120px] text-center">Tỷ Lệ Đạt</TableHead>
                <TableHead className="w-[120px] text-center">Đánh Giá</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fulfillment.items.map((it: any, idx: number) => (
                <TableRow key={it.orderDetailId || idx} className="hover:bg-slate-50/60">
                  <TableCell className="text-center font-mono text-slate-400">
                    {idx + 1}
                  </TableCell>
                  <TableCell className="font-mono font-medium text-slate-700">
                    <EntityLink entityType="product" id={it.productCode || it.orderDetailId} code={it.productCode} />
                  </TableCell>
                  <TableCell className="font-medium text-slate-900">
                    {it.productName}
                  </TableCell>
                  <TableCell className="text-center text-slate-600">
                    {it.unit}
                  </TableCell>
                  <TableCell className="text-right font-mono text-slate-600">
                    {it.orderedQty.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-mono font-bold text-emerald-600">
                    {it.deliveredQty.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-mono text-slate-500">
                    {it.remainingQty.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-mono font-semibold text-slate-800">
                    {formatMoney(it.totalDeliveredAmount)} đ
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center gap-1.5 justify-center">
                      <Progress
                        value={Math.min(100, it.completionRate)}
                        className="h-1.5 w-12"
                      />
                      <span className="font-mono text-[11px] font-semibold text-slate-700">
                        {it.completionRate}%
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    {it.inTolerance ? (
                      <span className="inline-flex items-center text-[10px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Đạt Chuẩn
                      </span>
                    ) : (
                      <span className="inline-flex items-center text-[10px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Thiếu Hàng
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* ═══ CƠ CẤU CHI PHÍ THỰC TẾ & DÒNG TIỀN THANH TOÁN (2 COLS) ═══ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Col 1: Bảng Cơ Cấu Chi Phí Thực Tế */}
        <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-xs space-y-3">
          <div className="flex items-center justify-between border-b border-slate-100 pb-2">
            <h4 className="font-semibold text-slate-800 text-xs flex items-center gap-1.5">
              <Calculator className="w-4 h-4 text-indigo-600" />
              Cơ Cấu Chi Phí Thực Tế Sản Xuất
            </h4>
            <span className="font-mono font-bold text-slate-800">
              {formatMoney(costAndProfit.actualTotalCost)} đ
            </span>
          </div>

          <div className="space-y-2 text-xs">
            <div className="flex justify-between items-center py-1 border-b border-slate-50">
              <span className="text-slate-600">1. Phôi giấy & NVL kho:</span>
              <span className="font-mono font-semibold text-slate-800">
                {formatMoney(costAndProfit.actualMaterialCost)} đ
              </span>
            </div>
            <div className="flex justify-between items-center py-1 border-b border-slate-50">
              <span className="text-slate-600">2. Giờ máy chạy xưởng (MES):</span>
              <span className="font-mono font-semibold text-slate-800">
                {formatMoney(costAndProfit.actualMachineCost)} đ
              </span>
            </div>
            <div className="flex justify-between items-center py-1 border-b border-slate-50">
              <span className="text-slate-600">3. Gia công ngoài (Outsource):</span>
              <span className="font-mono font-semibold text-slate-800">
                {formatMoney(costAndProfit.actualOutsourceCost)} đ
              </span>
            </div>
            <div className="flex justify-between items-center py-1 pt-2 font-bold text-slate-900 border-t border-slate-200">
              <span>Tổng Chi Phí:</span>
              <span className="font-mono text-rose-600">
                {formatMoney(costAndProfit.actualTotalCost)} đ
              </span>
            </div>
          </div>
        </div>

        {/* Col 2: Dòng Tiền & Phiếu Thu Đơn Hàng */}
        <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-xs space-y-3">
          <div className="flex items-center justify-between border-b border-slate-100 pb-2">
            <h4 className="font-semibold text-slate-800 text-xs flex items-center gap-1.5">
              <DollarSign className="w-4 h-4 text-emerald-600" />
              Dòng Tiền Đã Thu & Công Nợ
            </h4>
            <Button
              variant="outline"
              size="sm"
              className="h-6 px-2 text-[11px] text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
              onClick={() => setShowReceiptDialog(true)}
            >
              + Lập Phiếu Thu
            </Button>
          </div>

          <div className="space-y-2 text-xs">
            <div className="flex justify-between items-center py-1 border-b border-slate-50">
              <span className="text-slate-600">Tổng giá trị đơn hàng:</span>
              <span className="font-mono font-semibold text-slate-800">
                {formatMoney(financials.orderTotal)} đ
              </span>
            </div>
            <div className="flex justify-between items-center py-1 border-b border-slate-50">
              <span className="text-slate-600">Đã thanh toán (Thu tiền):</span>
              <span className="font-mono font-semibold text-emerald-600">
                {formatMoney(financials.totalPaid)} đ
              </span>
            </div>
            <div className="flex justify-between items-center py-1 border-b border-slate-50 font-bold">
              <span className="text-slate-700">Còn lại phải thu:</span>
              <span className="font-mono text-amber-600">
                {formatMoney(financials.balanceDue)} đ
              </span>
            </div>
            <div className="text-[11px] text-slate-500 pt-1">
              Trạng thái:{" "}
              <Badge variant="outline" className="text-[10px] font-semibold">
                {order.paymentStatus === "PAID"
                  ? "Đã thu đủ"
                  : order.paymentStatus === "PARTIAL"
                    ? "Thu một phần"
                    : "Chưa thanh toán"}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ DIALOG XÁC NHẬN ĐÓNG ĐƠN HÀNG ═══ */}
      <Dialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900 text-base">
              <CheckCircle2 className="w-5 h-5 text-indigo-600" />
              Xác Nhận Quyết Toán & Đóng Đơn Hàng
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Đơn hàng: <span className="font-semibold text-slate-800">{order.orderId}</span>
              <br />
              Hành động này sẽ chốt toàn bộ số liệu giao hàng, P&L thực tế và chuyển trạng thái đơn sang <strong className="text-slate-700">COMPLETED</strong>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-3 text-xs">
            <div className="bg-slate-50 p-3 rounded-lg space-y-1.5 border border-slate-200">
              <div className="flex justify-between">
                <span className="text-slate-500">Tổng đặt:</span>
                <span className="font-mono font-medium">{fulfillment.totalOrderedQty.toLocaleString()} sp</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Thực giao:</span>
                <span className="font-mono font-bold text-emerald-600">{fulfillment.totalDeliveredQty.toLocaleString()} sp ({fulfillment.overallFulfillmentRate}%)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Doanh thu chốt:</span>
                <span className="font-mono font-bold text-slate-900">{formatMoney(financials.realizedRevenue)} đ</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Lợi nhuận gộp:</span>
                <span className="font-mono font-bold text-emerald-600">{formatMoney(costAndProfit.grossProfit)} đ ({costAndProfit.grossMarginPct}%)</span>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700">
                Ghi chú quyết toán (Tùy chọn)
              </label>
              <Textarea
                rows={2}
                value={closeNotes}
                onChange={(e) => setCloseNotes(e.target.value)}
                placeholder="Nhập ý kiến chỉ đạo hoặc biên bản đối soát kèm theo..."
                className="text-xs resize-none"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              disabled={closeOrderMutation.isPending}
              onClick={() => setShowCloseConfirm(false)}
            >
              Hủy
            </Button>
            <Button
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
              disabled={closeOrderMutation.isPending}
              onClick={() => closeOrderMutation.mutate()}
            >
              {closeOrderMutation.isPending ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  Đang đóng đơn...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                  Xác Nhận Đóng Đơn Hàng
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ QUICK RECEIPT DIALOG ═══ */}
      <QuickReceiptDialog
        open={showReceiptDialog}
        onOpenChange={setShowReceiptDialog}
        source={{
          type: "ORDER",
          id: order.id,
          code: order.orderId,
          customerName: order.customer?.shortName || order.customer?.name,
          defaultAmount: financials.balanceDue,
        }}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["order-settlement", orderId] });
          queryClient.invalidateQueries({ queryKey: ["order", orderId] });
        }}
      />
    </div>
  );
}
